import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import '../styles/Header.css';
import Logo from '../website_images/Logos/primary_logo.png';
import Contact from '../website_images/Icons/contact.png';
import Language from '../website_images/Icons/language.png';
import ArrowIcon from '../website_images/Icons/arrow_down.png'
import { HashLink } from 'react-router-hash-link';

const getNavLinks = (t: (key: string) => string) => [
  {
    label: t('home'),
    path: '/',
    sublinks: [],
  },
  {
    label: t('companies'),
    path: '/unternehmen',
    sublinks: [
      { label: t('mission'), path: '/unternehmen#mission' },
      { label: t('brandCore'), path: '/unternehmen#brand' },
      { label: t('network'), path: '/unternehmen#network' },
      { label: t('team'), path: '/unternehmen#team' },
    ],
  },
  {
    label: t('projectReferences'),
    path: '/referenzen',
    sublinks: [
      { label: t('investmentOffers'), path: '/referenzen#investment-options' },
    ],
  },
  {
    label: t('investmentProducts'),
    path: '/investmentansatz',
    sublinks: [
      { label: t('productOverview'), path: '/investmentansatz?section=products' },
      { label: t('acquisition'), path: '/investmentansatz?section=acquisition' },
      { label: t('management'), path: '/investmentansatz?section=management' },
    ],
  },
  {
    label: t('contact'),
    path: '/kontakt',
    sublinks: [],
  },
];

  {/**
    sub links for project references
      { label: t('investmentOpportunities'), path: '/referenzen#investment-chances' },
      { label: t('investmentOffers'), path: '/referenzen#investment-options' },
      { label: t('references'), path: '/referenzen#investment-options' },
  */}

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Check localStorage first, then browser language, then fallback
    const stored = localStorage.getItem('i18nextLng');
    if (stored && (stored === 'de' || stored === 'en')) {
      return stored;
    }
    
    // Check browser language
    const browserLang = navigator.language || navigator.languages?.[0] || '';
    if (browserLang.startsWith('de')) return 'de';
    if (browserLang.startsWith('en')) return 'en';
    
    return 'de'; // fallback
  });
  
  const navLinks = getNavLinks(t);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setOpenDropdown(null);
    setExpandedSections(new Set());
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
    setExpandedSections(new Set());
  };

  const handleDropdown = (idx: number) => {
    setOpenDropdown(openDropdown === idx ? null : idx);
  };

  const toggleSection = (index: number) => {
    const newExpandedSections = new Set(expandedSections);
    if (newExpandedSections.has(index)) {
      newExpandedSections.delete(index);
    } else {
      newExpandedSections.add(index);
    }
    setExpandedSections(newExpandedSections);
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    setCurrentLanguage(language);
    setIsLanguageDropdownOpen(false);
    
    // Add language class to body for CSS targeting
    document.body.classList.remove('language-en', 'language-de');
    document.body.classList.add(`language-${language}`);
  };

  // Initialize language class on mount and listen for language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
      document.body.classList.remove('language-en', 'language-de');
      document.body.classList.add(`language-${lng}`);
    };

    // Set initial language immediately
    const initialLanguage = currentLanguage;
    
    // Make sure i18n is set to our detected language
    if (i18n.language !== initialLanguage) {
      i18n.changeLanguage(initialLanguage);
    }
    
    document.body.classList.remove('language-en', 'language-de');
    document.body.classList.add(`language-${initialLanguage}`);

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    // Also check after a short delay to catch any delayed detection
    const timeoutId = setTimeout(() => {
      const delayedLanguage = i18n.language || 'de';
      if (delayedLanguage !== currentLanguage) {
        setCurrentLanguage(delayedLanguage);
        document.body.classList.remove('language-en', 'language-de');
        document.body.classList.add(`language-${delayedLanguage}`);
      }
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, currentLanguage]);

  // Global click handler to close menu when clicking anywhere
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      if (isMenuOpen) {
        const menuButton = target.closest('.menu-button');
        const mobileNav = target.closest('.mobile-nav');
        
        // Don't close if clicking on menu button or inside mobile nav
        if (!menuButton && !mobileNav) {
          closeMenu();
        }
      }

      if (isLanguageDropdownOpen) {
        const languageButton = target.closest('.language-selector');
        const languageDropdown = target.closest('.language-dropdown');
        
        // Don't close if clicking on language button or inside dropdown
        if (!languageButton && !languageDropdown) {
          setIsLanguageDropdownOpen(false);
        }
      }
    };

    if (isMenuOpen || isLanguageDropdownOpen) {
      document.addEventListener('click', handleGlobalClick);
    }

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [isMenuOpen, isLanguageDropdownOpen]);

  return (
    <header className='header'>
      <div className='header-container'>
        <button className='menu-button' onClick={toggleMenu} aria-label="Toggle menu">
          <div className={`hamburger ${isMenuOpen ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        <Link to='/' className='logo'>
          <img src={Logo} alt="Votera Logo" className='header_logo'/>
        </Link>
        <nav className='desktop-nav'>
          {navLinks.map((nav, idx) => (
            <div className="nav-link-group" key={nav.label} onMouseEnter={() => handleDropdown(idx)} onMouseLeave={() => setOpenDropdown(null)}>
              <Link to={nav.path} className='nav-link'>{nav.label}</Link>
              {openDropdown === idx && (
                <div className="nav-sublinks">
                  {nav.sublinks.map(sublink => (
                    <Link to={sublink.path} className="nav-sublink" key={sublink.label}>
                      {sublink.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className='header-icons'>
          <Link to='/kontakt' className='nav-link'>
            <button className='icon-button' aria-label="Contact">
              <img src={Contact} alt="Contact Icon" className='icon-image'/>
            </button>
          </Link>
          <div className="language-selector">
            <button 
              className='icon-button' 
              aria-label="Language"
              onClick={toggleLanguageDropdown}
            >
              <img src={Language} alt="Language Icon" className='icon-image'/>
            </button>
            {isLanguageDropdownOpen && (
              <div className="language-dropdown">
                <div className="language-option" onClick={() => changeLanguage('de')}>
                  <div className="language-info">
                    <span className="language-name">Deutsch</span>
                    <span className="language-code">DE</span>
                  </div>
                  {currentLanguage === 'de' && <div className="selected-indicator">✓</div>}
                </div>
                <div className="language-option" onClick={() => changeLanguage('en')}>
                  <div className="language-info">
                    <span className="language-name">English</span>
                    <span className="language-code">EN</span>
                  </div>
                  {currentLanguage === 'en' && <div className="selected-indicator">✓</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className='mobile-menu-overlay'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.nav
              className='mobile-nav'
              initial={{ opacity: 0, x: '-100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '-100%' }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className='mobile-nav-header'>
                <Link to='/' className='mobile-nav-logo' onClick={closeMenu}>
                  <img src={Logo} alt="Votera Logo" className='mobile-logo'/>
                </Link>
                <button className='close-button' onClick={closeMenu} aria-label="Close menu">
                  <div className='close-icon'>
                    <span></span>
                    <span></span>
                  </div>
                </button>
              </div>
              
              <div className='mobile-nav-content'>
                {navLinks.map((nav, index) => (
                  <div key={nav.label} className="mobile-nav-section">
                    <div className='mobile-nav-main-link'>
                      <Link 
                        to={nav.path} 
                        className='nav-text-link'
                        onClick={closeMenu}
                      >
                        <span className='nav-text'>{nav.label}</span>
                      </Link>
                      {nav.sublinks.length > 0 && (
                        <button 
                          className='expand-button'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSection(index);
                          }}
                          aria-label={`Toggle ${nav.label} submenu`}
                        >
                          <span className={`expand-icon ${expandedSections.has(index) ? 'expanded' : ''}`}>
                            <img src={ArrowIcon} alt='arrow-icon'></img>
                          </span>
                        </button>
                      )}
                    </div>
                    
                    {nav.sublinks.length > 0 && (
                      <div className={`mobile-nav-sublinks ${expandedSections.has(index) ? 'expanded' : ''}`}>
                        {nav.sublinks.map(sublink => (
                          <HashLink 
                            to={sublink.path} 
                            className="mobile-nav-sublink" 
                            key={sublink.label} 
                            onClick={closeMenu}
                          >
                            {sublink.label}
                          </HashLink>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className='mobile-nav-footer'>
                <div className="mobile-language-selector">
                  <div className="mobile-language-options">
                    <div 
                      className={`mobile-language-option ${currentLanguage === 'de' ? 'active' : ''}`}
                      onClick={() => changeLanguage('de')}
                    >
                      <div className="language-info">
                        <span className="language-name">Deutsch</span>
                        <span className="language-code">DE</span>
                      </div>
                      {currentLanguage === 'de' && <div className="selected-indicator">✓</div>}
                    </div>
                    <div 
                      className={`mobile-language-option ${currentLanguage === 'en' ? 'active' : ''}`}
                      onClick={() => changeLanguage('en')}
                    >
                      <div className="language-info">
                        <span className="language-name">English</span>
                        <span className="language-code">EN</span>
                      </div>
                      {currentLanguage === 'en' && <div className="selected-indicator">✓</div>}
                    </div>
                  </div>
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;